import { useState, useRef, useEffect } from 'react';
import { useOrgAuth } from '../contexts/OrgAuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Z } from '../lib/zIndex';
import { UserAvatar } from './UserAvatar';

export default function UserMenu() {
  const { session, logout, switchOrganization } = useOrgAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on navigation
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  if (!session) return null;

  const userEmail = session.user.email || t('userMenu.emailMissing', 'Email missing');

  let tempName = session.user.name;
  if (!tempName || tempName === userEmail) {
    const localPart = userEmail.split('@')[0] || '';
    tempName = localPart.split('.').map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') || t('userMenu.user', 'User');
  }
  const userName = tempName;

  const userAvatar = session.user.avatar_url;
  const userId = session.user.id;

  const currentOrgId = session.current_organization?.id;
  const memberships = session.memberships || session.active_memberships || [];

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="w-9 h-9 rounded-full overflow-hidden hover:opacity-90 transition-opacity">
          {userAvatar ? (
            <img src={userAvatar} alt={userName} className="w-full h-full object-cover" />
          ) : (
            <UserAvatar userId={userId} name={userName} size="2xl" />
          )}
        </div>
      </button>

      {isOpen && (
        <div className={`absolute end-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-[${Z.modal}]`}>
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-medium text-slate-900 truncate">{userName}</p>
            <p className="text-xs text-slate-500 truncate">{userEmail}</p>
          </div>

          <div className="py-2 border-b border-slate-100">
            <button
              onClick={() => {
                navigate('/app');
                setIsOpen(false);
              }}
              className="w-full text-start px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 me-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {t('userMenu.goToDashboard', 'Go to Dashboard')}
            </button>
            <button
              onClick={() => {
                navigate('/app/settings/notifications');
                setIsOpen(false);
              }}
              className="w-full text-start px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center"
            >
              <Bell className="w-4 h-4 me-2 text-slate-400" />
              {t('userMenu.notificationSettings', 'Notification settings')}
            </button>
          </div>

          {memberships && memberships.length > 0 && (
            <div className="py-2 border-b border-slate-100">
              <p className="px-4 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {t('userMenu.organizations', 'Organizations')}
              </p>
              {memberships.map((membership) => (
                <button
                  key={membership.organization_id}
                  onClick={() => {
                    switchOrganization(membership.organization_id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-start px-4 py-2 text-sm transition-colors ${
                    currentOrgId === membership.organization_id
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{membership.organization_name || membership.organization_id}</span>
                    {currentOrgId === membership.organization_id && (
                      <svg className="w-4 h-4 text-primary-600 shrink-0 ms-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="pt-2">
            <button
              onClick={() => {
                logout();
                setIsOpen(false);
              }}
              className="w-full text-start px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              {t('userMenu.signOut', 'Sign out')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
